(ns leg.util
  "Small things several screens need, and none of them owns."
  (:require [clojure.string :as str]))

(defn oldest-first?
  "Which way round the lists run, from the word in the settings
  (`:reader/order`). Anything this window has not heard of is the
  newspaper's way round - the same division `leg.theme/face` makes for the
  face.

  The server has this rule too (`leser.articles/oldest-first?`) and it is
  the one that decides what a list contains; this copy is here because the
  window has to put a hand-placed row in the right spot
  (`leg.state/with-row`) and has to draw the setting as chosen. Two ends
  that share nothing but the shape of the EDN between them share the
  reading of a word by each knowing it, not by asking."
  [order]
  (= "oldest" order))

(defn images?
  "Whether the list shows the picture that came with an article
  (`:reader/images`). `\"off\"` is the only word that means anything;
  everything else, nil included, shows them - the same lenient reading
  `oldest-first?` gives the order.

  Shown unless somebody says otherwise, because these are the images of the
  articles themselves, already shown in the reader and already fetched the
  careful way (`referrerpolicy=\"no-referrer\"`, see `leser.html`). The
  switch is for somebody who wants a column of headlines, or who is paying
  for the megabytes."
  [images]
  (not= "off" images))

(defn start-feeds?
  "Whether a window that was opened on nothing in particular should come up
  on the list of feeds rather than on the articles of all of them
  (`:reader/start`). `\"feeds\"` is the only word that means anything;
  everything else, nil included, is the list of articles - the same lenient
  reading `oldest-first?` and `images?` give their words, and the one that
  keeps an older server's silence meaning what the reader has always done.

  A question only a narrow window asks. On a screen with room for three
  panes the feeds are beside the articles, so both answers are the same
  screen (`leg.state/start-pane!`)."
  [start]
  (= "feeds" start))

(defn- ->date [s]
  (when-let [s (and (string? s) (not-empty s) s)]
    (let [d (js/Date. s)]
      (when-not (js/isNaN (.getTime d)) d))))

(defn ago
  "How long ago, in as few words as it takes. A list of articles is read by
  their age and not by their timestamps - `2h` says what
  `2026-09-27T23:14:02Z` says, and says it in the width of a column."
  [s]
  (when-let [d (->date s)]
    (let [secs (max 0 (quot (- (.getTime (js/Date.)) (.getTime d)) 1000))
          mins (quot secs 60)
          hours (quot mins 60)
          days (quot hours 24)]
      (cond
        (< secs 60)  "now"
        (< mins 60)  (str mins "m")
        (< hours 24) (str hours "h")
        (< days 7)   (str days "d")
        (< days 365) (str (quot days 7) "w")
        :else        (str (quot days 365) "y")))))

(defn at
  "The date and the time of day, in as few characters as the machine's own
  format allows: `09.10.26, 20:04` where this is read in German, the same
  numbers in whatever order the reader's machine puts them.

  All numbers and not a spelled-out month, because this is the kicker line
  of a list row and the feed's name is on it too. Next to `ago`, which says
  the same thing as a width rather than as a time - a list can be read
  either way, and which one it shows is not this namespace's decision."
  [s]
  (when-let [d (->date s)]
    (.toLocaleString d js/undefined
                     #js {:dateStyle "short" :timeStyle "short"})))

(defn stamp
  "The full date, for the one place that has room for it: under the title
  of the article that is open. In the machine's own format, because
  whoever is reading it is where they are."
  [s]
  (when-let [d (->date s)]
    (.toLocaleString d js/undefined
                     #js {:dateStyle "medium" :timeStyle "short"})))

(defn host
  "The site an address belongs to, without the `www.` nobody reads."
  [url]
  (when (and (string? url) (not-empty url))
    (try
      (-> (js/URL. url) .-hostname (str/replace #"^www\." ""))
      (catch :default _ nil))))

(defn feed-label
  "What to call a feed on the screen: what this person renamed it to, or
  what it calls itself, or the site it is fetched from, or the address.
  Never nothing - a row with no name is a row nobody can press on purpose.

  Here and not in any one screen because three of them say it and they must
  all say the same: the name in the list of feeds, the masthead over the
  articles, and the dialog that asks which of them to mark read."
  [f]
  (or (not-empty (str/trim (str (:feed/title f))))
      (host (:feed/url f))
      (:feed/url f)))
